import { MoneyRequest, MoneyRequestPage, moneyCall, moneyRequestApi } from './moneyRequestApi';

/**
 * /finance/transfers, /finance/allowlist[/requests] and /finance/limits[/requests] (XBIT-4530).
 * The server signs and broadcasts an approved transfer; no signed transaction ever reaches the browser.
 */

export interface TransferBody {
  chain: string;
  asset: 'SOL' | 'USDC';
  fromAddress: string;
  toAddress: string;
  amount: string;
  reason?: string;
}

export interface AllowlistEntry {
  id: string;
  flow: string;
  chain: string;
  address: string;
  label: string | null;
  active: boolean;
  addedByRequestId: string;
  removedByRequestId: string | null;
  createdAt: string;
  removedAt: string | null;
}

export interface FinanceLimit {
  id: string;
  chain: string;
  asset: string;
  perTransferMax: string;
  rolling24hMax: string;
  /** Rolling-24h amount counted so far (executed + in flight). */
  used24h: string;
  updatedAt: string;
}

// ── Transfers ────────────────────────────────────────────────────────────────

const transfersApi = moneyRequestApi('finance/transfers');

export const listTransfers = transfersApi.list;
export const getTransfer = transfersApi.get;
export const cancelTransfer = transfersApi.cancel;
export const approveTransfer = transfersApi.approve;
export const rejectTransfer = transfersApi.reject;
export const requestTransfer = (body: TransferBody) => transfersApi.create(body);

/** Moves no funds: closes an UNKNOWN or stalled transfer once its source wallet nonce has advanced. */
export const reconcileTransfer = (id: string) =>
  moneyCall<MoneyRequest>('POST', `finance/transfers/${id}/reconcile`, {});

// ── Allowlist ────────────────────────────────────────────────────────────────

const allowlistRequests = moneyRequestApi('finance/allowlist/requests');

export const listAllowlistEntries = (params?: { page?: number; size?: number; active?: boolean }) =>
  moneyCall<{ data: AllowlistEntry[]; total: number }>(
    'GET',
    'finance/allowlist',
    undefined,
    params
  );
export const listAllowlistRequests = allowlistRequests.list;
export const requestAllowlistChange = (body: {
  action: 'ADD' | 'REMOVE';
  chain: string;
  address: string;
  label?: string;
  reason?: string;
}) => allowlistRequests.create(body);
export const cancelAllowlistRequest = allowlistRequests.cancel;
export const approveAllowlistRequest = allowlistRequests.approve;
export const rejectAllowlistRequest = allowlistRequests.reject;

// ── Limits ───────────────────────────────────────────────────────────────────

const limitRequests = moneyRequestApi('finance/limits/requests');

export const listLimits = () =>
  moneyCall<{ data: FinanceLimit[]; total: number }>('GET', 'finance/limits');
export const listLimitRequests = limitRequests.list;
export const requestLimitChange = (body: {
  chain: string;
  asset: string;
  perTransferMax: string;
  rolling24hMax: string;
  reason?: string;
}) => limitRequests.create(body);
export const cancelLimitRequest = limitRequests.cancel;
export const approveLimitRequest = limitRequests.approve;
export const rejectLimitRequest = limitRequests.reject;

export type { MoneyRequest, MoneyRequestPage };
